import { useState } from "react";

export default function FooterSection() {
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [inquiry, setInquiry] = useState({ name: "", email: "", message: "" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inquiry.name || !inquiry.email || !inquiry.message) return;
    setFormStatus("sending");
    try {
      const body = new URLSearchParams();
      body.append("name", inquiry.name);
      body.append("email", inquiry.email);
      body.append("message", inquiry.message);

      const res = await fetch("https://readdy.ai/api/form/d7fjhpvimhg6tri2vdm0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (res.ok) {
        setFormStatus("done");
        setInquiry({ name: "", email: "", message: "" });
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-stone-800 text-white">
      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14">
          {/* Left: Brand + Info */}
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/97140e49-74bc-47a8-9097-71ae89eff5f5.png"
              alt="후쿠오카 한울"
              className="h-10 sm:h-12 w-auto object-contain mb-5 sm:mb-6 brightness-[10] opacity-90"
            />
            <p className="text-stone-400 text-sm leading-relaxed mb-5 sm:mb-6 max-w-xs">
              한국인이 운영하는 우리집 같은 후쿠오카 숙소.<br />
              일본어 걱정 없이 즐기는 진짜 후쿠오카 여행.
            </p>

            <div className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
              <div className="flex items-center gap-3 text-stone-400 text-sm">
                <div className="w-8 h-8 flex items-center justify-center bg-stone-700 rounded-lg shrink-0">
                  <i className="ri-kakao-talk-fill text-yellow-400"></i>
                </div>
                카카오톡: @후쿠오카한울
              </div>
              <div className="flex items-center gap-3 text-stone-400 text-sm">
                <div className="w-8 h-8 flex items-center justify-center bg-stone-700 rounded-lg shrink-0">
                  <i className="ri-instagram-line text-rose-400"></i>
                </div>
                인스타그램: @fukuoka_hanul
              </div>
              <div className="flex items-center gap-3 text-stone-400 text-sm">
                <div className="w-8 h-8 flex items-center justify-center bg-stone-700 rounded-lg shrink-0">
                  <i className="ri-mail-line text-amber-400"></i>
                </div>
                hello@fukuoka-hanul.com
              </div>
            </div>

            <div className="flex flex-wrap gap-3 sm:gap-4 text-sm">
              {["#best15", "#host-story", "#booking-guide", "#trust"].map((href, i) => {
                const labels = ["숙소 리스트", "호스트 스토리", "예약 가이드", "이용 후기"];
                return (
                  <button
                    key={href}
                    onClick={() => handleNavClick(href)}
                    className="text-stone-400 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    {labels[i]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Contact Form */}
          <div>
            <h3 className="text-lg font-bold mb-1">문의하기</h3>
            <p className="text-stone-400 text-sm mb-5 sm:mb-6">숙소, 예약, 특별 요청 무엇이든 물어보세요.</p>

            <form data-readdy-form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs text-stone-400 mb-1.5">이름</label>
                  <input
                    type="text"
                    name="name"
                    value={inquiry.name}
                    onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })}
                    placeholder="홍길동"
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-4 py-2.5 text-sm text-white placeholder-stone-500 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-stone-400 mb-1.5">이메일</label>
                  <input
                    type="email"
                    name="email"
                    value={inquiry.email}
                    onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })}
                    placeholder="your@email.com"
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-4 py-2.5 text-sm text-white placeholder-stone-500 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-stone-400 mb-1.5">문의 내용</label>
                <textarea
                  name="message"
                  value={inquiry.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setInquiry({ ...inquiry, message: e.target.value });
                  }}
                  placeholder="문의하실 내용을 자유롭게 적어주세요. (최대 500자)"
                  rows={4}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-4 py-2.5 text-sm text-white placeholder-stone-500 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                />
                <p className="text-xs text-stone-500 text-right mt-1">{inquiry.message.length} / 500</p>
              </div>

              {formStatus === "done" ? (
                <div className="flex items-center gap-2 text-green-400 text-sm bg-green-900/30 border border-green-700 rounded-lg px-4 py-3">
                  <i className="ri-checkbox-circle-line"></i>
                  문의가 접수되었습니다. 빠른 시일 내에 연락 드릴게요!
                </div>
              ) : formStatus === "error" ? (
                <div className="flex items-center gap-2 text-rose-400 text-sm bg-rose-900/30 border border-rose-700 rounded-lg px-4 py-3">
                  <i className="ri-error-warning-line"></i>
                  전송에 실패했습니다. 다시 시도해 주세요.
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={formStatus === "sending"}
                  className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-stone-600 text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  {formStatus === "sending" ? "전송 중..." : "문의 보내기"}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-700">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-stone-500 text-xs">
          <p>© 2026 후쿠오카 한울 (Fukuoka Hanul). All rights reserved.</p>
          <p>한국인이 운영하는 우리집 같은 후쿠오카 숙소 큐레이션 서비스</p>
        </div>
      </div>
    </footer>
  );
}
